// useSessionDetail — PowerSync-backed hook for the
// `GET /capture/sessions/:id` payload consumed by both
// BuildingCaptureScreen and RoomCaptureScreen. Replaces the inline
// `fetchSessionDetail` REST helper.
//
// The hook returns the same `SessionDetail` shape the REST endpoint
// returns (10-api.md §5.2): { session, spaces, siteItems? }.
//
// Template synthesis (the load-bearing complication):
// The REST endpoint synthesises "template-only" rows on the server
// from `room_type_assets` so each space shows its expected asset
// list even before the user has captured anything. Offline, that
// merge has to happen client-side. This hook does it in JS over
// data queried locally:
//
//   1. capture_items (the user's actual rows)
//   2. asset_types / asset_sub_systems / asset_systems (so each row
//      carries System / SubSystem labels)
//   3. spaces (the building's rooms; loaded with floor + room-type)
//   4. room_type_assets (the templates per room type)
//
// For each space we walk its room type's templates, attach a real
// capture_items row when one exists, otherwise emit a synthesised
// `isTemplateOnly: true` row with id ''. Real-but-not-template items
// (field-added asset variants captured here) appear afterwards.
//
// All queries are reactive — adding a capture_items row via the
// PowerSync write path triggers a re-render with the new row in the
// merged output.

import { useMemo } from 'react'

import { useQuery } from '@powersync/react'

import {
  type CaptureItemRow,
  type GroupingConvention,
  type SessionDetail,
  type SessionSummary,
  type SpaceWithItems,
  type UnitOfMeasure,
} from '@citycare/shared'
import { SESSION_SPACES_SQL } from './session-spaces-sql'

interface SessionSqlRow {
  id: string
  site_id: string
  building_id: string | null
  user_id: string
  user_display_name: string
  started_at: string
  completed_at: string | null
  source_device_id: string | null
  synced_at: string | null
  sync_version: number
  created_at: string
  updated_at: string
  // JOINs
  site_name: string
  site_base_id: string
  site_base_name: string
  building_name: string | null
  building_floc: string | null
  building_status: string | null
}

interface SpaceSqlRow {
  id: string
  building_id: string
  floor_id: string | null
  floor_name: string | null
  room_type_id: string
  room_type_name: string
  serial_number: number
  display_name: string
  alias: string | null
  is_pseudo: number
  is_field_added: number
  requires_review: number
}

interface CaptureItemSqlRow {
  id: string
  capture_session_id: string
  site_id: string
  building_id: string | null
  space_id: string | null
  asset_type_id: string
  asset_type_name: string
  unit_of_measure: string
  grouping_convention: string
  is_field_added: number
  requires_review: number
  sub_system_id: string
  sub_system_name: string
  system_id: string
  system_name: string
  quantity: string
  is_zero_confirmed: number
  notes: string | null
  captured_at: string
  updated_at: string
  sync_version: number
}

interface TemplateAssetSqlRow {
  room_type_id: string
  asset_type_id: string
  asset_type_name: string
  unit_of_measure: string
  grouping_convention: string
  is_field_added: number
  requires_review: number
  sub_system_id: string
  sub_system_name: string
  system_id: string
  system_name: string
  template_display_order: number
}

export interface UseSessionDetailResult {
  detail: SessionDetail | null
  isLoading: boolean
  isFetching: boolean
  error: Error | undefined
}

const IDLE: UseSessionDetailResult = {
  detail: null,
  isLoading: false,
  isFetching: false,
  error: undefined,
}

/**
 * Loads SessionDetail for a session id. Pass an empty string to keep
 * the hook idle (zero queries fired) — useful for screens that have
 * a route param that may not be set yet.
 */
export function useSessionDetail(sessionId: string): UseSessionDetailResult {
  const sessionResult = useQuery<SessionSqlRow>(
    `SELECT
       cs.id, cs.site_id, cs.building_id, cs.user_id, cs.user_display_name,
       cs.started_at, cs.completed_at, cs.source_device_id, cs.synced_at,
       cs.sync_version, cs.created_at, cs.updated_at,
       s.name AS site_name,
       s.base_id AS site_base_id,
       bs.name AS site_base_name,
       b.name AS building_name,
       b.floc AS building_floc,
       b.status AS building_status
     FROM capture_sessions cs
     INNER JOIN sites s ON cs.site_id = s.id
     INNER JOIN bases bs ON s.base_id = bs.id
     LEFT JOIN buildings b ON cs.building_id = b.id
     WHERE cs.id = ?
     LIMIT 1`,
    [sessionId],
  )

  const buildingId = sessionResult.data[0]?.building_id ?? ''

  const spacesResult = useQuery<SpaceSqlRow>(SESSION_SPACES_SQL, [buildingId])

  const itemsResult = useQuery<CaptureItemSqlRow>(
    `SELECT
       ci.id,
       ci.capture_session_id,
       ci.site_id,
       ci.building_id,
       ci.space_id,
       ci.asset_type_id,
       at.name AS asset_type_name,
       at.unit_of_measure,
       at.grouping_convention,
       at.is_field_added,
       at.requires_review,
       ass.id AS sub_system_id,
       ass.name AS sub_system_name,
       asys.id AS system_id,
       asys.name AS system_name,
       ci.quantity,
       ci.is_zero_confirmed,
       ci.notes,
       ci.captured_at,
       ci.updated_at,
       ci.sync_version
     FROM capture_items ci
     INNER JOIN asset_types at ON ci.asset_type_id = at.id
     INNER JOIN asset_sub_systems ass ON at.sub_system_id = ass.id
     INNER JOIN asset_systems asys ON ass.system_id = asys.id
     WHERE ci.capture_session_id = ?
       -- Mirror the server's canonical filter (get-session.ts:259): an admin
       -- Reject soft-deletes the row via capture_items.rejected_at. PR 713
       -- lets a rejected tombstone coexist with a live row in one slot; without
       -- this the offline mirror renders the rejected count as live (and it
       -- could be re-incremented). Sibling issue 708.
       AND ci.rejected_at IS NULL`,
    [sessionId],
  )

  // Template assets per room type — one row per (room_type, asset_type)
  // pair. The space → room_type mapping comes from the spaces query
  // above; we load all templates for room types that have at least
  // one space in this building so the merge can find them in O(1).
  const templatesResult = useQuery<TemplateAssetSqlRow>(
    `SELECT
       rta.room_type_id,
       rta.asset_type_id,
       at.name AS asset_type_name,
       at.unit_of_measure,
       at.grouping_convention,
       at.is_field_added,
       at.requires_review,
       ass.id AS sub_system_id,
       ass.name AS sub_system_name,
       asys.id AS system_id,
       asys.name AS system_name,
       rta.display_order AS template_display_order
     FROM room_type_assets rta
     INNER JOIN asset_types at ON rta.asset_type_id = at.id
     INNER JOIN asset_sub_systems ass ON at.sub_system_id = ass.id
     INNER JOIN asset_systems asys ON ass.system_id = asys.id
     WHERE rta.room_type_id IN (
       SELECT DISTINCT room_type_id FROM spaces WHERE building_id = ?
     )
       -- Hide admin soft-deleted (rejected_at) asset types from the capture
       -- template, mirroring the server (get-session.ts:309-311) and the
       -- site path (use-site-items.ts:269-271). The sync rule ships ALL
       -- asset_types (SELECT * FROM asset_types) and a soft-delete leaves the
       -- room_type_assets mapping intact, so WITHOUT this filter a rejected
       -- asset type still renders as a template-only row the field user can
       -- capture. The create then 400s invalid_argument ("soft-rejected and
       -- cannot be captured") which (pre PR 769) wedges the whole upload queue
       -- and (post PR 769) needlessly quarantines a count that can never land.
       -- Filter all three classification levels so a rejected sub-system or
       -- system also drops its descendant template rows.
       AND at.rejected_at IS NULL
       AND ass.rejected_at IS NULL
       AND asys.rejected_at IS NULL
     ORDER BY rta.display_order ASC, at.name ASC`,
    [buildingId],
  )

  const detail = useMemo<SessionDetail | null>(() => {
    if (sessionId === '') return null
    if (sessionResult.data.length === 0) return null
    const sessionRow = sessionResult.data[0] as SessionSqlRow
    const session = projectSession(sessionRow)
    const isSiteScoped = sessionRow.building_id === null

    if (isSiteScoped) {
      // Site-scoped sessions only need siteItems — no spaces, no
      // template synthesis. The screen filters by grouping convention
      // itself; we surface every item that belongs to the session.
      const siteItems = itemsResult.data
        .filter((row) => row.building_id === null && row.space_id === null)
        .map(projectItem)
      return { session, spaces: [], siteItems }
    }

    // Building-scoped: index items by space_id for O(n+k) merge.
    const itemsBySpace = new Map<string, CaptureItemSqlRow[]>()
    for (const row of itemsResult.data) {
      if (row.space_id === null) continue
      const arr = itemsBySpace.get(row.space_id) ?? []
      arr.push(row)
      itemsBySpace.set(row.space_id, arr)
    }
    // Index templates by room_type_id.
    const templatesByRoomType = new Map<string, TemplateAssetSqlRow[]>()
    for (const t of templatesResult.data) {
      const arr = templatesByRoomType.get(t.room_type_id) ?? []
      arr.push(t)
      templatesByRoomType.set(t.room_type_id, arr)
    }

    const spaces: SpaceWithItems[] = spacesResult.data.map((sp) =>
      buildSpaceWithItems(
        sp,
        itemsBySpace.get(sp.id) ?? [],
        templatesByRoomType.get(sp.room_type_id) ?? [],
      ),
    )
    return { session, spaces }
  }, [
    sessionId,
    sessionResult.data,
    spacesResult.data,
    itemsResult.data,
    templatesResult.data,
  ])

  if (sessionId === '') return IDLE

  const isLoading =
    sessionResult.isLoading ||
    spacesResult.isLoading ||
    itemsResult.isLoading ||
    templatesResult.isLoading
  const isFetching =
    sessionResult.isFetching ||
    spacesResult.isFetching ||
    itemsResult.isFetching ||
    templatesResult.isFetching
  const error =
    sessionResult.error ??
    spacesResult.error ??
    itemsResult.error ??
    templatesResult.error

  return { detail, isLoading, isFetching, error }
}

function projectSession(row: SessionSqlRow): SessionSummary {
  return {
    id: row.id,
    siteId: row.site_id,
    buildingId: row.building_id,
    userId: row.user_id,
    userDisplayName: row.user_display_name,
    startedAt: row.started_at,
    completedAt: row.completed_at,
    sourceDeviceId: row.source_device_id,
    syncedAt: row.synced_at,
    syncVersion: row.sync_version,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    site: {
      id: row.site_id,
      name: row.site_name,
      baseId: row.site_base_id,
      baseName: row.site_base_name,
    },
    building:
      row.building_id !== null && row.building_name !== null
        ? {
            id: row.building_id,
            name: row.building_name,
            floc: row.building_floc,
            // pgEnum maps to TEXT in SQLite; the consumer narrows
            status: (row.building_status ?? 'not_started') as SessionSummary['building'] extends {
              status: infer S
            }
              ? S
              : never,
          }
        : null,
  }
}

function projectItem(row: CaptureItemSqlRow): CaptureItemRow {
  return {
    id: row.id,
    assetTypeId: row.asset_type_id,
    assetTypeName: row.asset_type_name,
    unitOfMeasure: row.unit_of_measure as UnitOfMeasure,
    groupingConvention: row.grouping_convention as GroupingConvention,
    systemId: row.system_id,
    systemName: row.system_name,
    subSystemId: row.sub_system_id,
    subSystemName: row.sub_system_name,
    quantity: row.quantity,
    isZeroConfirmed: row.is_zero_confirmed === 1,
    notes: row.notes,
    capturedAt: row.captured_at,
    updatedAt: row.updated_at,
    syncVersion: row.sync_version,
    isFieldAdded: row.is_field_added === 1,
    requiresReview: row.requires_review === 1,
    isTemplateOnly: false,
  }
}

function projectTemplateOnly(t: TemplateAssetSqlRow): CaptureItemRow {
  return {
    id: '',
    assetTypeId: t.asset_type_id,
    assetTypeName: t.asset_type_name,
    unitOfMeasure: t.unit_of_measure as UnitOfMeasure,
    groupingConvention: t.grouping_convention as GroupingConvention,
    systemId: t.system_id,
    systemName: t.system_name,
    subSystemId: t.sub_system_id,
    subSystemName: t.sub_system_name,
    quantity: '0',
    isZeroConfirmed: false,
    notes: null,
    capturedAt: '',
    updatedAt: '',
    syncVersion: 0,
    isFieldAdded: t.is_field_added === 1,
    requiresReview: t.requires_review === 1,
    isTemplateOnly: true,
  }
}

function buildSpaceWithItems(
  sp: SpaceSqlRow,
  itemsForSpace: CaptureItemSqlRow[],
  templatesForRoomType: TemplateAssetSqlRow[],
): SpaceWithItems {
  // Index actual items by asset_type_id so template lookup is O(1).
  // Field-added rows captured in this space (asset_type_id NOT in the
  // template) are surfaced after the template entries.
  const itemsByAssetType = new Map<string, CaptureItemSqlRow>()
  for (const it of itemsForSpace) {
    itemsByAssetType.set(it.asset_type_id, it)
  }

  const merged: CaptureItemRow[] = []
  const usedAssetTypeIds = new Set<string>()
  for (const t of templatesForRoomType) {
    const real = itemsByAssetType.get(t.asset_type_id)
    if (real !== undefined) {
      merged.push(projectItem(real))
      usedAssetTypeIds.add(t.asset_type_id)
    } else {
      merged.push(projectTemplateOnly(t))
    }
  }
  for (const it of itemsForSpace) {
    if (!usedAssetTypeIds.has(it.asset_type_id)) {
      merged.push(projectItem(it))
    }
  }

  return {
    id: sp.id,
    floorId: sp.floor_id,
    floorName: sp.floor_name,
    roomTypeId: sp.room_type_id,
    roomTypeName: sp.room_type_name,
    serialNumber: sp.serial_number,
    displayName: sp.display_name,
    alias: sp.alias,
    isPseudo: sp.is_pseudo === 1,
    isFieldAdded: sp.is_field_added === 1,
    requiresReview: sp.requires_review === 1,
    items: merged,
  }
}
